import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Wifi, WifiOff, PauseCircle, Volume2, VolumeX, Trophy } from 'lucide-react';
import { api, connectWS, inr } from '../api';

const CONFETTI_COLORS = ['#FDE047', '#F9A8D4', '#7DD3FC', '#86EFAC', '#FDBA74', '#D8B4FE', '#F87171'];

let audioCtx = null;
const getCtx = () => {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
};

const playBell = (fanfare = false) => {
  try {
    const ctx = getCtx();
    const notes = fanfare ? [523.25, 659.25, 783.99, 1046.5, 1318.5] : [880, 1108.73, 1318.51];
    notes.forEach((f, i) => {
      const t = ctx.currentTime + i * (fanfare ? 0.16 : 0.12);
      [1, 2.76].forEach((h, hi) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = f * h;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(hi ? 0.08 : 0.35, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
        o.connect(g).connect(ctx.destination);
        o.start(t);
        o.stop(t + 1.7);
      });
    });
  } catch {}
};

function Confetti() {
  const pieces = Array.from({ length: 90 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    duration: 2.4 + Math.random() * 1.8,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 7 + Math.random() * 8,
    rotate: Math.random() * 360,
  }));
  return (
    <div className="fixed inset-0 pointer-events-none z-[90] overflow-hidden" data-testid="confetti-overlay">
      {pieces.map((p, i) => (
        <span key={i} className="confetti-piece" style={{
          left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
          background: p.color, width: p.size, height: p.size * 0.55,
          transform: `rotate(${p.rotate}deg)`,
        }} />
      ))}
    </div>
  );
}

export default function TVDashboard() {
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(new Date());
  const [sale, setSale] = useState(null);
  const [crown, setCrown] = useState(null);
  const [ceremony, setCeremony] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const saleTimer = useRef(null);
  const ceremonyTimer = useRef(null);
  const soundRef = useRef(true);
  soundRef.current = soundOn;

  const load = useCallback(() => api.get('/tv-stats').then((r) => setStats(r.data)), []);

  useEffect(() => {
    load();
    api.get('/leaderboard/current').then((r) => setCrown(r.data.crown));
    const unlock = () => getCtx();
    window.addEventListener('click', unlock, { once: true });
    const clock = setInterval(() => setNow(new Date()), 1000);
    const poll = setInterval(() => {
      load();
      api.get('/leaderboard/current').then((r) => setCrown(r.data.crown));
    }, 30000);
    const ws = connectWS((m) => {
      if (m.type === 'sale') {
          setSale(m);
          load();
          if (soundRef.current) playBell(false);
          clearTimeout(saleTimer.current);
          saleTimer.current = setTimeout(() => setSale(null), 8000);
        } else if (m.type === 'crown') {
          setCrown(m);
          setCeremony(true);
          if (soundRef.current) playBell(true);
          clearTimeout(ceremonyTimer.current);
          ceremonyTimer.current = setTimeout(() => setCeremony(false), 10000);
        } else if (m.type === 'presence' || m.type === 'availability' || m.type === 'leads-synced') {
          load();
        }
    });
    return () => {
      clearInterval(clock); clearInterval(poll); ws.stop();
      clearTimeout(saleTimer.current); clearTimeout(ceremonyTimer.current);
      window.removeEventListener('click', unlock);
    };
  }, [load]);

  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <main className="min-h-[calc(100vh-64px)] bg-[#12131A] p-6 md:p-10 relative" data-testid="tv-dashboard">
      <button
        data-testid="tv-sound-toggle"
        onClick={() => { setSoundOn(!soundOn); if (!soundOn) { getCtx(); playBell(false); } }}
        className={`absolute top-5 right-5 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${soundOn ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-slate-800 border-slate-600 text-slate-400'}`}
        title={soundOn ? 'Sound on — bell rings on every sale' : 'Sound muted'}
      >
        {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />} {soundOn ? 'Bell On' : 'Muted'}
      </button>
      <div className="text-center mb-6">
        <p className="font-hand text-2xl text-amber-300">Star & Daisy · Sales Floor</p>
        <h2 className="font-display font-extrabold text-3xl md:text-5xl text-white tracking-tight" data-testid="tv-date">{dateStr}</h2>
        <p className="font-display font-bold text-xl text-slate-400 mt-1">{timeStr}</p>
      </div>

      {crown && (
        <div className="flex justify-center mb-8" data-testid="tv-crown-strip">
          <div className={`flex items-center gap-3 bg-gradient-to-r from-amber-500/20 via-amber-400/25 to-amber-500/20 border border-amber-400/50 rounded-full px-6 py-2.5 ${ceremony ? 'animate-bounce' : ''}`}>
            <Trophy size={22} className="text-amber-300" />
            <p className="font-display font-extrabold text-lg md:text-2xl text-amber-200">
              👑 Top Seller of the Day: {crown.agent_name} · {inr(crown.amount)}
            </p>
            <Trophy size={22} className="text-amber-300" />
          </div>
        </div>
      )}

      {(() => {
        const board = (stats?.agents || []).filter((a) => a.target > 0)
          .sort((x, y) => y.brownie_balance - x.brownie_balance);
        if (board.length === 0) return null;
        const medals = ['🥇', '🥈', '🥉'];
        return (
          <div className="flex justify-center mb-8" data-testid="tv-points-leaderboard">
            <div className="bg-[#1C1E28] border border-slate-700 rounded-2xl px-5 py-3 max-w-5xl w-full">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-400 text-center mb-2">🍫 Brownie Points Leaderboard</p>
              <div className="flex flex-wrap justify-center gap-3">
                {board.map((a, i) => (
                  <div key={a.id} data-testid={`tv-leaderboard-rank-${i + 1}`}
                    className={`flex items-center gap-2 rounded-full px-4 py-1.5 border ${i === 0 ? 'bg-amber-400/15 border-amber-400/60' : i === 1 ? 'bg-slate-300/10 border-slate-400/50' : i === 2 ? 'bg-orange-500/10 border-orange-500/50' : 'bg-slate-800/60 border-slate-700'}`}>
                    <span className="text-xl leading-none">{medals[i] || <span className="text-xs font-extrabold text-slate-500">#{i + 1}</span>}</span>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-extrabold text-slate-900 border border-white/40" style={{ background: a.avatar_color }}>
                      {a.name.split(' ').map((w) => w[0]).join('')}
                    </span>
                    <span className={`font-display font-extrabold text-sm ${i === 0 ? 'text-amber-200' : 'text-white'}`}>{a.name.split(' ')[0]}</span>
                    <span className={`text-sm font-extrabold ${a.brownie_balance >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {a.brownie_balance >= 0 ? '+' : ''}{Math.round(a.brownie_balance).toLocaleString('en-IN')} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {(sale || ceremony) && <Confetti />}
      {ceremony && !sale && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[95] bg-amber-400 text-slate-900 rounded-2xl px-10 py-5 sticky-shadow rotate-[1deg] text-center animate-bounce" data-testid="crown-banner">
          <p className="font-display font-extrabold text-3xl md:text-4xl">👑 {crown?.agent_name} is today's TOP SELLER!</p>
          <p className="font-hand text-2xl">{inr(crown?.amount)} in sales · take a bow! 🏆</p>
        </div>
      )}
      {sale && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[95] bg-amber-400 text-slate-900 rounded-2xl px-8 py-4 sticky-shadow rotate-[-1deg] text-center animate-bounce" data-testid="sale-banner">
          <p className="font-display font-extrabold text-2xl md:text-3xl">🎉 {sale.agent_name} just closed {inr(sale.amount)}!</p>
          <p className="font-hand text-xl">{sale.customer_name} · ring the bell! 🔔</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {(stats?.agents || []).map((a) => {
          const celebrating = sale && sale.agent_id === a.id;
          const crowned = crown && crown.agent_id === a.id;
          return (
            <div key={a.id} data-testid={`tv-agent-card-${a.id}`}
              className={`relative rounded-2xl p-6 border-2 transition-all duration-500 ${celebrating ? 'bg-amber-400 border-amber-300 scale-105 shadow-[0_0_60px_rgba(253,224,71,0.45)]' : crowned ? 'bg-[#1C1E28] border-amber-400 shadow-[0_0_35px_rgba(253,224,71,0.2)]' : 'bg-[#1C1E28] border-slate-700'}`}>
              {crowned && (
                <span data-testid={`tv-crown-${a.id}`} className={`absolute -top-5 left-1/2 -translate-x-1/2 text-4xl drop-shadow-[0_2px_6px_rgba(253,224,71,0.6)] ${ceremony ? 'animate-bounce' : ''}`}>👑</span>
              )}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-display font-extrabold text-base text-slate-900 border-2 border-white/40" style={{ background: a.avatar_color }}>
                  {a.name.split(' ').map((w) => w[0]).join('')}
                </div>
                <div className="min-w-0">
                  <p className={`font-display font-extrabold text-xl truncate ${celebrating ? 'text-slate-900' : 'text-white'}`}>{a.name}</p>
                  {(() => {
                    const idleMin = a.online && a.last_seen ? Math.floor((Date.now() - new Date(a.last_seen).getTime()) / 60000) : 0;
                    if (a.online && idleMin >= 10) {
                      return (
                        <p data-testid={`tv-idle-${a.id}`} className={`text-xs font-extrabold flex items-center gap-1 ${celebrating ? 'text-slate-800' : 'text-orange-400 animate-pulse'}`}>
                          😴 Idle for {idleMin}m
                        </p>
                      );
                    }
                    return (
                      <p className={`text-xs font-bold flex items-center gap-1 ${celebrating ? 'text-slate-800' : a.online ? (a.available ? 'text-emerald-400' : 'text-amber-400') : 'text-slate-500'}`}>
                        {a.online ? (a.available ? <><Wifi size={11} /> Online</> : <><PauseCircle size={11} /> Paused</>) : <><WifiOff size={11} /> Offline</>}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`rounded-xl p-3 text-center ${celebrating ? 'bg-white/40' : 'bg-sky-500/10 border border-sky-500/20'}`}>
                  <p className={`font-display font-extrabold text-3xl ${celebrating ? 'text-slate-900' : 'text-sky-300'}`}>{a.open_leads}</p>
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${celebrating ? 'text-slate-700' : 'text-sky-500'}`}>Open Leads</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${celebrating ? 'bg-white/40' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
                  <p className={`font-display font-extrabold text-3xl ${celebrating ? 'text-slate-900' : 'text-emerald-300'}`}>{a.closed_leads}</p>
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${celebrating ? 'text-slate-700' : 'text-emerald-500'}`}>Closed</p>
                </div>
              </div>
              <div className={`rounded-xl p-3 text-center ${celebrating ? 'bg-slate-900' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                <p className={`font-display font-extrabold text-3xl md:text-4xl ${celebrating ? 'text-amber-300' : 'text-amber-300'}`} data-testid={`tv-sales-${a.id}`}>{inr(a.sales_value)}</p>
                <p className={`text-[10px] font-bold uppercase tracking-widest ${celebrating ? 'text-amber-500' : 'text-amber-600'}`}>Sales Value</p>
              </div>
              {a.target > 0 && (
                <div className="mt-3" data-testid={`tv-brownie-${a.id}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${celebrating ? 'text-slate-700' : 'text-slate-500'}`}>Today {Math.round(a.progress)}%</span>
                    <span className={`text-sm font-extrabold px-2 py-0.5 rounded-full ${a.brownie_balance >= 0 ? (celebrating ? 'bg-emerald-600 text-white' : 'bg-emerald-500/15 text-emerald-300') : (celebrating ? 'bg-red-600 text-white' : 'bg-red-500/15 text-red-300')}`}>
                      {a.brownie_balance >= 0 ? '🍫 +' : '🍩 '}{Math.round(a.brownie_balance).toLocaleString('en-IN')} pts
                    </span>
                  </div>
                  <div className={`h-2.5 rounded-full overflow-hidden ${celebrating ? 'bg-white/40' : 'bg-slate-700/60'}`}>
                    <div className="h-full rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(a.progress, 100)}%`, background: a.progress >= 100 ? 'linear-gradient(90deg,#10B981,#34D399)' : 'linear-gradient(90deg,#FB923C,#FDE047)' }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-center font-hand text-xl text-slate-600 mt-10">every sale gets a party 🎊 · live from SND CRM</p>
    </main>
  );
}
